// Phase 19 · Community V2 · DM privacy + Blocked list settings page
// Phase 22b · Email digest preference (sandbox mode)
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock, Users, UserMinus, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const CHOICES = [
    { value: "everyone", label: "Everyone", desc: "Anyone on the platform can DM you." },
    { value: "followers", label: "Followers only", desc: "Only accounts you follow (or that follow you) can DM you." },
    { value: "none", label: "Nobody", desc: "You will not receive DMs. Existing conversations remain visible." },
];

const DIGEST_CHOICES = [
    { value: "off", label: "Off", desc: "No email digest. You will still see in-app notifications." },
    { value: "daily", label: "Daily", desc: "One recap email per day, delivered at 8am your local time." },
    { value: "weekly", label: "Weekly", desc: "One recap email every Monday morning summarising the past week." },
];

export default function CommunitySettings() {
    const { user } = useAuth();
    const [current, setCurrent] = useState("everyone");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [blocked, setBlocked] = useState([]);
    // Phase 22b digest preference
    const [digest, setDigest] = useState("off");
    const [digestAvailable, setDigestAvailable] = useState(false);
    const [digestSaving, setDigestSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API}/api/community/settings/dm-privacy`, { credentials: "include" });
            if (r.ok) {
                const d = await r.json();
                setCurrent(d.receive_from || "everyone");
            }
            const b = await fetch(`${API}/api/community/settings/blocked-list`, { credentials: "include" });
            if (b.ok) {
                const d = await b.json();
                setBlocked(d.items || []);
            }
            // Phase 22b · digest preference (kill-switch aware: 503 → hide the section)
            const dp = await fetch(`${API}/api/notifications/digest-preference`, { credentials: "include" });
            if (dp.ok) {
                const d = await dp.json();
                setDigest(d.frequency || "off");
                setDigestAvailable(true);
            } else if (dp.status !== 503) {
                // Non-503 error → still hide but do not spam toast
                setDigestAvailable(false);
            }
        } catch {}
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const save = async (val) => {
        setSaving(true);
        try {
            const r = await fetch(`${API}/api/community/settings/dm-privacy`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ receive_from: val }),
            });
            if (r.ok) {
                setCurrent(val);
                toast.success("DM privacy updated");
            } else {
                toast.error("Update failed");
            }
        } catch { toast.error("Network error"); }
        setSaving(false);
    };

    const unblock = async (username) => {
        try {
            const r = await fetch(`${API}/api/community/settings/unblock`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ blocked_username: username }),
            });
            if (r.ok) {
                toast.success(`@${username} unblocked`);
                load();
            } else toast.error("Unblock failed");
        } catch { toast.error("Network error"); }
    };

    // Phase 22b · save digest preference
    const saveDigest = async (val) => {
        setDigestSaving(true);
        try {
            const r = await fetch(`${API}/api/notifications/digest-preference`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ frequency: val }),
            });
            if (r.ok) {
                setDigest(val);
                toast.success("Digest preference updated");
            } else {
                toast.error("Update failed");
            }
        } catch { toast.error("Network error"); }
        setDigestSaving(false);
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter" data-testid="community-settings-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to="/community" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Community
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <h1 className="text-base font-semibold">Community settings</h1>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-6 space-y-8">
                {/* DM privacy */}
                <section className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-1">
                        <Lock size={16} className="text-[#FFFFFF]" />
                        <h2 className="text-sm font-semibold text-neutral-100">Direct messages</h2>
                    </div>
                    <p className="text-xs text-neutral-500 mb-4">
                        Choose who can send you DMs. Applies immediately.
                    </p>
                    {loading ? (
                        <p className="text-sm text-neutral-500">Loading…</p>
                    ) : (
                        <div className="space-y-2">
                            {CHOICES.map((c) => (
                                <label
                                    key={c.value}
                                    className={`flex items-start gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${current === c.value ? "border-[#FFFFFF] bg-[#FFFFFF]/10" : "border-[#1A1A1A] hover:bg-white/5"}`}
                                >
                                    <input
                                        type="radio"
                                        name="dm-privacy"
                                        checked={current === c.value}
                                        onChange={() => save(c.value)}
                                        disabled={saving}
                                        className="accent-[#FFFFFF] mt-0.5"
                                        data-testid={`dm-privacy-${c.value}`}
                                    />
                                    <div>
                                        <p className="text-sm font-semibold text-neutral-100">{c.label}</p>
                                        <p className="text-xs text-neutral-500">{c.desc}</p>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </section>

                {/* Phase 22b · Email digest preference (sandbox-mode logging only) */}
                {digestAvailable && (
                    <section className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-5" data-testid="digest-preference-card">
                        <div className="flex items-center gap-2 mb-1">
                            <Mail size={16} className="text-[#CCCCCC]" />
                            <h2 className="text-sm font-semibold text-neutral-100">Email digest</h2>
                        </div>
                        <p className="text-xs text-neutral-500 mb-4">
                            Get a summary of community activity delivered to your inbox. Sandbox mode: emails
                            are logged for review before we flip the live sender.
                        </p>
                        <div className="space-y-2">
                            {DIGEST_CHOICES.map((c) => (
                                <label
                                    key={c.value}
                                    className={`flex items-start gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${digest === c.value ? "border-[#CCCCCC] bg-[#CCCCCC]/10" : "border-[#1A1A1A] hover:bg-white/5"}`}
                                >
                                    <input
                                        type="radio"
                                        name="digest-frequency"
                                        checked={digest === c.value}
                                        onChange={() => saveDigest(c.value)}
                                        disabled={digestSaving}
                                        className="accent-[#CCCCCC] mt-0.5"
                                        data-testid={`digest-frequency-${c.value}`}
                                    />
                                    <div>
                                        <p className="text-sm font-semibold text-neutral-100">{c.label}</p>
                                        <p className="text-xs text-neutral-500">{c.desc}</p>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </section>
                )}

                {/* Blocked users */}
                <section className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-1">
                        <UserMinus size={16} className="text-[#FAFAFA]" />
                        <h2 className="text-sm font-semibold text-neutral-100">Blocked users</h2>
                    </div>
                    <p className="text-xs text-neutral-500 mb-4">
                        Blocked users cannot DM you or see your community posts, and vice-versa.
                    </p>
                    {!blocked.length ? (
                        <p className="text-xs text-neutral-500 italic">Nobody blocked</p>
                    ) : (
                        <ul className="divide-y divide-[#1A1A1A]">
                            {blocked.map((b) => (
                                <li key={b.blocked_user_id} className="py-2 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-neutral-900 font-bold text-xs">
                                            {b.blocked_username?.[0]?.toUpperCase() || "?"}
                                        </div>
                                        <div>
                                            <p className="text-sm text-neutral-200">@{b.blocked_username}</p>
                                            {b.reason && <p className="text-[10px] text-neutral-500">{b.reason}</p>}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => unblock(b.blocked_username)}
                                        className="text-xs text-[#FFFFFF] hover:text-[#CCCCCC]"
                                        data-testid={`unblock-${b.blocked_username}`}
                                    >
                                        Unblock
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <p className="text-[10px] text-neutral-600 flex items-center gap-1 justify-center">
                    <Users size={11} /> Signed in as @{user?.username || user?.email}
                </p>
            </main>
        </div>
    );
}
